import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { EmptyArray } from 'lib/array/arrayUtils'
import styled from 'styled-components'
import { rem } from 'polished'
import { useInView } from 'react-intersection-observer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import Sheet from 'components/utils/Sheet'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import Caption from 'LuxKit/Typography/Caption'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import clsx from 'clsx'
import GeoContext from 'contexts/geoContext'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import zIndex from 'styles/tools/z-index'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { getRegionSaveAmount } from 'luxPlus/lib/getRegionSaveAmountCopy'
import { dateDifference, isSameDay, useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useLuxPlusLandingActions from 'luxPlus/hooks/useLuxPlusLandingActions'
import { useLuxPlusPromoBanners } from 'luxPlus/hooks/useLuxPlusPromos'
import { fetchTotalPotentialSavings } from 'actions/LuxPlusActions'
import ModalContext from 'contexts/ModalContext'
import LuxPlusPotentialSavingsModal from '../modals/LuxPlusPotentialSavingsModal'
import LuxPlusLogo from '../Logos/LuxPlusLogo'
import LuxPlusStatCards from './LuxPlusStatCards'
import LuxPlusPromoBannerCarousel from './LuxPlusPromoBannerCarousel'
import LuxPlusLandingAboutSection from './LuxPlusLandingAboutSection'
import LuxPlusLandingBenefitsSection from './LuxPlusLandingBenefitsSection'
import LuxPlusLandingPartnerOffersTabSection from './LuxPlusLandingPartnerOffersTabSection'
import LuxPlusLandingNavigationContext, { type LuxPlusLandingNonMemberTab } from 'luxPlus/contexts/LuxPlusLandingTabContext'
import focusStyle from 'styles/utils/focusStyle'
const HeroSheet = styled(Sheet)`
  background: linear-gradient(90deg, #304DAB 0%, #4C82DF 100%);
`

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(32)};
  align-items: center;

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
  }
`

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    align-items: flex-start;
  }
`

const StatCardsWrapper = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    display: flex;
    flex-direction: column;
    gap: ${rem(24)};
  }
`

const MobileStatCards = styled.div`
  display: block;

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const Footnote = styled(Caption)`
  opacity: 0.8;
  max-width: ${rem(300)};
  margin: 0 auto;
  text-align: center;

  ${mediaQueryUp.tablet} {
    max-width: none;
    white-space: nowrap;
  }
`

const PreviewInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(6)};
  padding: ${rem(16)};
  border: 1px solid ${props => props.theme.palette.highlight.primary.normalForeground};
  border-radius: ${props => props.theme.borderRadius.M};
  backdrop-filter: blur(10px);
  width: 100%;
  
  ${mediaQueryUp.tablet} {
    max-width: ${rem(480)};
  }
`

const NavSection = styled(Pane)`
  position: sticky;
  z-index: ${zIndex.stickyHeaderBanner};
  top: 0;
  padding: ${rem(32)} 0 ${rem(20)} 0;
`

const ViewToggle = styled.div`
  display: flex;
  background: ${props => props.theme.palette.neutral.default.six};
  border-radius: ${rem(20)};
  padding: ${rem(4)};
  gap: ${rem(4)};
`

const ToggleButton = styled.button`
  padding: ${rem(8)} ${rem(20)};
  border: none;
  border-radius: ${rem(16)};
  font-size: ${rem(14)};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: transparent;
  color: ${props => props.theme.palette.neutral.default.three};

  &:hover {
    color: ${props => props.theme.palette.neutral.default.two};
  }

  &:focus-visible {
    ${focusStyle}
  }

  &.active {
    background: ${props => props.theme.palette.neutral.default.eight};
    color: ${props => props.theme.palette.neutral.default.one};
    box-shadow: ${props => props.theme.shadow.bottom.small};

    &:hover {
      color: ${props => props.theme.palette.neutral.default.one};
    }
  }
`

function LuxPlusFreePreviewLandingPageContent() {
  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()
  const showModal = useContext(ModalContext)
  const { currentRegionCode } = useContext(GeoContext)
  const saveAmount = getRegionSaveAmount(currentRegionCode)

  const dispatch = useAppDispatch()
  const [stickyRef, notSticky] = useInView()
  const [activeTab, setActiveTab] = useState<LuxPlusLandingNonMemberTab>('about')

  const firstName = useAppSelector(state => state.auth.account.givenName)

  const freePreviewExpiryDate = useAppSelector(
    state => state.auth.account.luxPlus.member.subscription.item?.expiryDate || '',
  )

  const totalPotentialSavings = useAppSelector(state => state.luxPlus.totalPotentialSavings.data)
  const personalSaving = totalPotentialSavings?.totalAmount
  const savingsItems: Array<App.LuxPlusPotentialSavingsItem> = totalPotentialSavings?.items ?? EmptyArray

  useEffect(() => {
    dispatch(fetchTotalPotentialSavings())
  }, [dispatch])

  const { handleBecomeMember } = useLuxPlusLandingActions()
  const promoBanners = useLuxPlusPromoBanners()

  const handlePastBookingsClick = useCallback(() => {
    if (savingsItems.length > 0 && personalSaving !== undefined) {
      showModal(<LuxPlusPotentialSavingsModal items={savingsItems} totalSaving={personalSaving} />)
    }
  }, [showModal, savingsItems, personalSaving])

  const switchToTab = useCallback((tab: LuxPlusLandingNonMemberTab) => {
    setActiveTab(tab)
  }, [])

  const handleTabAbout = useCallback(() => switchToTab('about'), [switchToTab])
  const handleTabBenefits = useCallback(() => switchToTab('benefits'), [switchToTab])
  const handleTabPartnerOffers = useCallback(() => switchToTab('partner-offers'), [switchToTab])

  const navigationContextValue = useMemo(() => ({
    navigateToPartnerOffers: handleTabPartnerOffers,
  }), [handleTabPartnerOffers])

  const previewPillCopy = useMemo(() => {
    const diff = dateDifference(new Date(freePreviewExpiryDate))
    const expiresToday = isSameDay(new Date(), new Date(freePreviewExpiryDate))
    const days = expiresToday ? 0 : (diff.days <= 1 ? 1 : diff.days)

    return intl.formatMessage(
      {
        id: 'ERHTeQ0VNNMIYOiSxqZZ',
        defaultMessage: '{days, plural, =0 {Preview ends today} one {Preview ends in # day} other {Preview ends in # days}}',
        description: 'Pill label showing how many days until free preview period expires',
      },
      { days },
    )
  }, [freePreviewExpiryDate, intl])

  const subheading = useMemo(() => intl.formatMessage(
    {
      id: '7Zr2eoICwSlVtCPmu03W',
      defaultMessage: 'Your free preview is available until {date} or when you make your first {programName} purchase.',
      description: 'Subheading explaining free preview availability and expiry on landing page',
    },
    { date: intlDateFormatter(freePreviewExpiryDate, INTL_DMY_CASUAL_SHORT_FORMAT), programName: LUXURY_PLUS.PROGRAM_NAME },
  ), [freePreviewExpiryDate, intlDateFormatter, intl])

  const heroHeading = intl.formatMessage({
    id: 'PbM6R8laZR046SdFkaP9',
    defaultMessage: 'Your VIP Travel Club',
    description: 'Hero heading on LuxPlus free preview landing page',
  })

  const heroSubheading = intl.formatMessage({
    id: 'r1Grlvg9sKgYZW9txrwt',
    defaultMessage: 'Unpack a world of member-only benefits.',
    description: 'Hero subheading on LuxPlus free preview landing page',
  })

  const aboutTabLabel = intl.formatMessage({
    id: '44kT2MggRLwSErniPcPV',
    defaultMessage: 'About',
    description: 'Tab label for the About section on LuxPlus landing page',
  })

  const benefitsTabLabel = intl.formatMessage({
    id: '5SpkviQdtWDk6OcsoqkP',
    defaultMessage: 'Benefits',
    description: 'Tab label for the Benefits section on LuxPlus landing page',
  })

  const partnerOffersTabLabel = intl.formatMessage({
    id: 'R-9iZhfbVJvWCOce6x8A',
    defaultMessage: 'Partner Offers',
    description: 'Tab label for the Partner Offers section on LuxPlus landing page',
  })

  return (
    <>
      <HeroSheet padding="40 20" paletteType="inverse">
        <LayoutContainer>
          <HeroGrid>
            <LeftColumn>
              <LuxPlusLogo height={20} />
              <HeadingTextBlock variant="heading1">{heroHeading}</HeadingTextBlock>
              <BodyText variant="medium" colour="neutral-one">
                {heroSubheading}
              </BodyText>
              <PreviewInfoWrapper>
                <BodyText variant="medium" weight="bold" colour="highlight-primary">
                  {previewPillCopy}
                </BodyText>
                <BodyText variant="medium" colour="neutral-one">
                  {subheading}
                </BodyText>
              </PreviewInfoWrapper>
              <MobileStatCards>
                <LuxPlusStatCards saveAmount={saveAmount} firstName={firstName} personalSaving={personalSaving} onPastBookingsClick={handlePastBookingsClick} />
              </MobileStatCards>
              <TextButton kind="primary" onClick={handleBecomeMember}>
                {intl.formatMessage({
                  id: 'FQH1tIR8HDpYMBX6Czah',
                  defaultMessage: 'Become a member',
                  description: 'Button to start LuxPlus membership purchase on free preview landing page',
                })}
              </TextButton>
              <CSSBreakpoint max="mobile">
                <Footnote variant="medium" colour="neutral-one">
                  {intl.formatMessage({
                    id: 'FfQ9bv-qgIrSVby6q7Uw',
                    defaultMessage: '*Based on savings by top 50% of active {programName} members over 12 months',
                    description: 'Footnote disclaimer about LuxPlus savings statistics on free preview landing page',
                  }, { programName: LUXURY_PLUS.PROGRAM_NAME })}
                </Footnote>
              </CSSBreakpoint>
            </LeftColumn>
            <StatCardsWrapper>
              <LuxPlusStatCards saveAmount={saveAmount} firstName={firstName} personalSaving={personalSaving} onPastBookingsClick={handlePastBookingsClick} />
              <CSSBreakpoint min="mobile">
                <Footnote variant="medium" colour="neutral-one">
                  {intl.formatMessage({
                    id: 'FfQ9bv-qgIrSVby6q7Uw',
                    defaultMessage: '*Based on savings by top 50% of active {programName} members over 12 months',
                    description: 'Footnote disclaimer about LuxPlus savings statistics on free preview landing page',
                  }, { programName: LUXURY_PLUS.PROGRAM_NAME })}
                </Footnote>
              </CSSBreakpoint>
            </StatCardsWrapper>
          </HeroGrid>
        </LayoutContainer>
      </HeroSheet>

      {promoBanners.length > 0 && <LuxPlusPromoBannerCarousel items={promoBanners} />}

      <NavSection shadow={!notSticky ? 'bottom-small' : undefined}>
        <LayoutContainer variant="medium">
          <Group direction="horizontal" horizontalAlign="center">
            <ViewToggle>
              <ToggleButton
                className={clsx({ active: activeTab === 'about' })}
                onClick={handleTabAbout}
                type="button"
              >
                {aboutTabLabel}
              </ToggleButton>
              <ToggleButton
                className={clsx({ active: activeTab === 'benefits' })}
                onClick={handleTabBenefits}
                type="button"
              >
                {benefitsTabLabel}
              </ToggleButton>
              <ToggleButton
                className={clsx({ active: activeTab === 'partner-offers' })}
                onClick={handleTabPartnerOffers}
                type="button"
              >
                {partnerOffersTabLabel}
              </ToggleButton>
            </ViewToggle>
          </Group>
        </LayoutContainer>
      </NavSection>
      <div ref={stickyRef} />

      <LuxPlusLandingNavigationContext.Provider value={navigationContextValue}>
        {activeTab === 'about' && <LuxPlusLandingAboutSection />}
        {activeTab === 'benefits' && <LuxPlusLandingBenefitsSection />}
        {activeTab === 'partner-offers' && <LuxPlusLandingPartnerOffersTabSection />}
      </LuxPlusLandingNavigationContext.Provider>
    </>
  )
}

export default LuxPlusFreePreviewLandingPageContent
